import { useSeoMeta } from '@unhead/react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { ArrowLeftIcon, ChevronDownIcon } from 'lucide-react';
import { v4 as uuidv4 } from '@/lib/uuid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Layout } from '@/components/Layout';
import { ListItemEditor, type ListFormItem } from '@/components/ListItemEditor';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { NIP51_KINDS, getKindConfig, SET_LIST_KINDS, resolveTagValue } from '@/lib/nip51';
import type { LinkList } from '@/hooks/useLinkLists';

export function CreateListPage() {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const forkFrom = (location.state as { forkFrom?: LinkList } | null)?.forkFrom;

  const [selectedKind, setSelectedKind] = useState(forkFrom?.kind ?? 30003);
  const kindConfig = getKindConfig(selectedKind)!;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [items, setItems] = useState<ListFormItem[]>([]);

  useSeoMeta({
    title: forkFrom ? 'Fork List — Nostr Links' : 'Create List — Nostr Links',
    description: 'Create a new NIP-51 list on Nostr.',
  });

  // Populate from fork
  useEffect(() => {
    if (forkFrom) {
      setSelectedKind(forkFrom.kind);
      setTitle(forkFrom.title ? `${forkFrom.title} (forked)` : '');
      setDescription(forkFrom.description);
      setImage(forkFrom.image);
      setHashtags(forkFrom.hashtags.join(', '));
      setItems(
        forkFrom.items.map((item, i) => ({
          id: String(i),
          tag: item.tag,
          value: item.value,
          label: item.label,
        }))
      );
    }
  }, [forkFrom]);

  // Reset items when kind changes (tag types may differ)
  const handleKindChange = (kind: number) => {
    setSelectedKind(kind);
    setItems([]);
  };

  if (!user) {
    return (
      <Layout>
        <div className="container max-w-2xl py-16 text-center">
          <h1 className="text-2xl font-bold mb-2">Sign In Required</h1>
          <p className="text-muted-foreground">
            You need to sign in with your Nostr key to create a list.
          </p>
        </div>
      </Layout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validItems = items.filter((i) => i.value.trim().length > 0);
    if (validItems.length === 0) {
      toast({ title: 'Error', description: 'Add at least one item to your list.', variant: 'destructive' });
      return;
    }

    const isSet = SET_LIST_KINDS.includes(selectedKind);
    const dTag = isSet ? `nostree-${uuidv4()}` : undefined;

    const tags: string[][] = [];

    if (dTag) {
      tags.push(['d', dTag]);
    }

    if (title.trim()) {
      tags.push(['title', title.trim()]);
    }

    if (description.trim()) {
      tags.push(['description', description.trim()]);
    }

    if (image.trim()) {
      tags.push(['image', image.trim()]);
    }

    // Nostree-specific labels (only for kind 30003)
    if (selectedKind === 30003) {
      tags.push(['L', 'me.nostree.ontology']);
      tags.push(['l', 'nostree']);
    }

    // Add list items
    for (const item of validItems) {
      const rawValue = resolveTagValue(item.value);
      if (item.tag === 'r') {
        const label = item.label.trim() || rawValue;
        tags.push(['r', rawValue, label]);
      } else if (item.tag === 'relay') {
        tags.push(['relay', rawValue]);
      } else if (item.tag === 'word') {
        tags.push(['word', rawValue.toLowerCase()]);
      } else {
        // p, e, a
        tags.push([item.tag, rawValue]);
      }
    }

    // Hashtags (only for kinds that support them)
    const parsedTags = hashtags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    for (const t of parsedTags) {
      tags.push(['t', t]);
    }

    // NIP-31 alt tag for custom / non-obvious kinds
    if (selectedKind !== 1 && selectedKind !== 30003) {
      tags.push(['alt', `NIP-51 ${kindConfig.name}: ${title.trim() || kindConfig.description}`]);
    }

    try {
      await publish({ kind: selectedKind, content: '', tags });

      queryClient.invalidateQueries({ queryKey: ['nostr', 'link-lists'] });
      toast({ title: 'Published!', description: `Your ${kindConfig.name} has been published.` });

      const npub = nip19.npubEncode(user.pubkey);
      navigate(`/${npub}`);
    } catch {
      toast({ title: 'Error', description: 'Failed to publish list.', variant: 'destructive' });
    }
  };

  // Split kinds into sets and standard lists for dropdown grouping
  const setKinds = NIP51_KINDS.filter((k) => k.isSet);
  const standardKinds = NIP51_KINDS.filter((k) => !k.isSet);
  const showImageField = SET_LIST_KINDS.includes(selectedKind);

  return (
    <Layout>
      <div className="container max-w-2xl py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeftIcon className="size-4" />
          Back
        </button>

        <h1 className="text-2xl font-bold mb-6">
          {forkFrom ? 'Fork List' : 'Create New List'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* List Type Selector */}
          <div className="space-y-2">
            <Label>List Type</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal"
                >
                  <span className="flex items-center gap-2">
                    <span>{kindConfig.icon}</span>
                    <span>{kindConfig.name}</span>
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      — {kindConfig.description}
                    </span>
                  </span>
                  <ChevronDownIcon className="size-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-full min-w-[320px]" align="start">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Sets (multiple per user)</DropdownMenuLabel>
                {setKinds.map((k) => (
                  <DropdownMenuItem
                    key={k.kind}
                    onClick={() => handleKindChange(k.kind)}
                    className="flex flex-col items-start gap-0.5 py-2"
                  >
                    <span className="font-medium">{k.icon} {k.name}</span>
                    <span className="text-xs text-muted-foreground">{k.description}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">Standard Lists (one per user)</DropdownMenuLabel>
                {standardKinds.map((k) => (
                  <DropdownMenuItem
                    key={k.kind}
                    onClick={() => handleKindChange(k.kind)}
                    className="flex flex-col items-start gap-0.5 py-2"
                  >
                    <span className="font-medium">{k.icon} {k.name}</span>
                    <span className="text-xs text-muted-foreground">{k.description}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="text-xs text-muted-foreground">
              Kind {selectedKind} · {kindConfig.isSet ? 'Addressable (can have multiple)' : 'Replaceable (one per user)'}
            </p>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title{kindConfig.isSet ? '' : ' (optional)'}
            </Label>
            <Input
              id="title"
              placeholder={kindConfig.name}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required={kindConfig.isSet}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder={`A brief description of this ${kindConfig.name.toLowerCase()}...`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {/* Cover Image — only for sets */}
          {showImageField && (
            <div className="space-y-2">
              <Label htmlFor="image">Cover Image URL (optional)</Label>
              <Input
                id="image"
                placeholder="https://example.com/image.jpg"
                value={image}
                onChange={(e) => setImage(e.target.value)}
              />
            </div>
          )}

          {/* Hashtags */}
          <div className="space-y-2">
            <Label htmlFor="hashtags">Tags (comma-separated, optional)</Label>
            <Input
              id="hashtags"
              placeholder="bitcoin, nostr, tech"
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
            />
          </div>

          {/* Dynamic item editor */}
          <ListItemEditor
            kindConfig={kindConfig}
            items={items}
            onChange={setItems}
          />

          {/* Submit */}
          <Button type="submit" size="lg" className="w-full" disabled={isPending}>
            {isPending
              ? 'Publishing...'
              : forkFrom
                ? `Publish Forked ${kindConfig.name}`
                : `Publish ${kindConfig.name}`}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
